import React, { useState, Suspense } from 'react';
import { LoadingSkeleton } from '@helpCenter/view/common/loading';
import { useIntl } from 'react-intl-next';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu--next';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EditorSettingsIcon from '@atlaskit/icon/glyph/editor/settings';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@atlassian/help-center-common-component/constants';
import { MultiHcDropdownContent } from '../multi-hc-dropdown-content';
import { NoUnderlineDropdownItemWithAnalytics } from '../no-underline-dropdown-item-with-analytics';
import messages from './messages';

interface Props {
    currentHcName: string;
}
export const MultiHcDropdown: React.FC<Props> = ({ currentHcName }) => {
    const { formatMessage } = useIntl();
    const [isSeparatorVisible, setIsSeparatorVisible] = useState(true);

    const toggleSeparatorVisibility = (isVisible: boolean) => {
        setIsSeparatorVisible(isVisible);
    };

    const helpCenterManagementScreenUrl = HELP_CENTERS_MANAGEMENT_BASE_PATH;
    const helpCenterManagementScreenWithNewHcCreated = `${helpCenterManagementScreenUrl}?create=true`;

    // Number of help centers to be rendered in the loading skeleton
    const HELP_CENTER_LOADING_SKELETON_LENGTH = 2;
    const renderSuspense = (helpCenterLoadingSkeletonLength: number) => {
        return (
            <>
                {Array(helpCenterLoadingSkeletonLength)
                    .fill(1)
                    .map((_, index) => {
                        return (
                            <Box xcss={skeletonPaddingContainer} key={`multiHCLoader_${index}`}>
                                <LoadingSkeleton
                                    height={16}
                                    backgroundColor={token('color.skeleton.subtle', colors.N20)}
                                />
                            </Box>
                        );
                    })}
            </>
        );
    };
    return (
        <AnalyticsContext data={{ actionSubjectId: 'helpCenterMenu' }}>
            <DropdownMenu trigger={currentHcName}>
                <Suspense fallback={renderSuspense(HELP_CENTER_LOADING_SKELETON_LENGTH)}>
                    <MultiHcDropdownContent toggleSeparatorVisibility={toggleSeparatorVisibility} />
                </Suspense>
                <DropdownItemGroup hasSeparator={isSeparatorVisible}>
                    <AnalyticsContext data={{ actionSubjectId: 'manageHelpCenter' }}>
                        <NoUnderlineDropdownItemWithAnalytics
                            href={helpCenterManagementScreenUrl}
                            elemBefore={<EditorSettingsIcon label="" />}
                        >
                            {formatMessage(messages.manageHc)}
                        </NoUnderlineDropdownItemWithAnalytics>
                    </AnalyticsContext>
                    <AnalyticsContext data={{ actionSubjectId: 'createHelpCenter' }}>
                        <NoUnderlineDropdownItemWithAnalytics
                            href={helpCenterManagementScreenWithNewHcCreated}
                            elemBefore={<EditorAddIcon label="" />}
                        >
                            {formatMessage(messages.createHc)}
                        </NoUnderlineDropdownItemWithAnalytics>
                    </AnalyticsContext>
                </DropdownItemGroup>
            </DropdownMenu>
        </AnalyticsContext>
    );
};

const skeletonPaddingContainer = xcss({
    paddingBottom: 'space.100',
    paddingLeft: 'space.200',
    paddingRight: 'space.200',
    paddingTop: 'space.100',
});
